import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ContentsSelectors } from '@data/contents/Contents.selectors';
import * as React from 'react';
import { getContent } from '@data/contents/Contents.thunk';
import useAppDispatch from '@data/useAppDispatch';
import useContent from '@view/content-management-system/hooks/useContent';
import { Content, ContentTypes } from '@data/contents/Contents.model';
import ArticleView from '@view/discover-page/components/ArticleView';
import WithSideBar from '@shared/templates/with-side-bar/WithSideBar';
import SeriesView from '@view/discover-page/components/SeriesView';
import ArticleViewSkeleton from '@view/discover-page/components/ArticleViewSkeleton';
import SeriesViewSkeleton from '@view/discover-page/components/SeriesViewSkeleton';
import SurveyView from '@view/discover-page/components/SurveyView';

const ContentView: React.FC<unknown> = () => {
  const { contentId } = useParams();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(true);
  const [contentIsFetched, setContentIsFetched] = React.useState(false);

  const content: Content = useSelector((state: any) =>
    ContentsSelectors.contents.selectById(state.contents, contentId || -1),
  ) as Content;

  React.useEffect(() => {
    if (!contentIsFetched && contentId) {
      dispatch(
        getContent({
          id: contentId,
        }),
      ).then(() => {
        setIsLoading(false);
        setContentIsFetched(true);
      });
    }
  }, [contentId, contentIsFetched, dispatch, setIsLoading]);

  const {
    id,
    contentType,
    contentLabelType,
    title,
    cover,
    readTime,
    noOfSteps,
    description,
    steps,
    isStarted,
    noOfCompletedSteps,
    contentMark,
    bookmarked,
    seriesDetails,
  } = useContent(content);
  const safeId = id || '';
  const safeTitle = title || '';
  const safeCover = cover || '';
  const safeReadTime = readTime || '';
  const safeHtmlContent = content?.htmlContent || '';
  const safeArticleType = content?.articleType || 'html';
  const safePdfContent = content?.pdfContent;

  return (
    <WithSideBar>
      <div className="flex flex-col items-center justify-start w-full h-full pt-4 overflow-auto">
        {contentType.value === ContentTypes.ARTICLE &&
          (isLoading ? (
            <ArticleViewSkeleton />
          ) : (
            <ArticleView
              title={safeTitle}
              cover={safeCover}
              htmlContent={safeHtmlContent}
              contentType={contentLabelType}
              articleType={safeArticleType}
              pdfContent={safePdfContent}
              readTime={safeReadTime}
              id={safeId}
              contentMark={contentMark}
              bookmarked={bookmarked}
              seriesDetails={seriesDetails}
              isPreview={false}
            />
          ))}
        {contentType.value === ContentTypes.SERIES &&
          (isLoading ? (
            <SeriesViewSkeleton />
          ) : (
            <SeriesView
              cover={safeCover}
              title={safeTitle}
              numOfSteps={noOfSteps}
              contentType={contentLabelType}
              description={description}
              steps={steps}
              isStarted={isStarted}
              numOfCompletedSteps={noOfCompletedSteps}
              id={safeId}
              contentMark={contentMark}
              bookmarked={bookmarked}
            />
          ))}
        {contentType.value === ContentTypes.QUIZ &&
          (isLoading ? (
            <ArticleViewSkeleton />
          ) : (
            <div style={{ height: '100%', width: '60%', overflow: 'auto' }}>
              <SurveyView
                content={content}
                title={content.title}
                summary={content.description}
                surveyLink={content.surveyLink}
                category={content.category}
              />
            </div>
          ))}
      </div>
    </WithSideBar>
  );
};

export default ContentView;
